import moment from 'moment-timezone';

const DEFAULT_LOCALE = 'en-GB';
const DEFAULT_CURRENCY = 'GBP';

// Intl.NumberFormat throws RangeError on a malformed currency code (anything
// that isn't a valid ISO 4217 letter triple) or an unknown locale. Both
// values flow in from per-tenant ClientConfig, so a single bad cell in
// dbo.client_setting would white-screen every booking/POS surface that
// renders a price. Pre-validate the inputs and fall back to en-GB/GBP rather
// than letting the throw bubble up to React's unmount-the-tree handler.
const ISO_CURRENCY_RE = /^[A-Za-z]{3}$/;
const safeCurrency = (currency: string | null | undefined): string =>
    typeof currency === 'string' && ISO_CURRENCY_RE.test(currency)
        ? currency.toUpperCase()
        : DEFAULT_CURRENCY;

const buildCurrencyFormat = (
    currency: string,
    locale: string,
    extra: Intl.NumberFormatOptions = {},
): Intl.NumberFormat => {
    const safe = safeCurrency(currency);
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: safe,
            currencyDisplay: 'narrowSymbol',
            ...extra,
        });
    } catch {
        // Locale may also be invalid (e.g. an empty string saved in
        // locale.locale). Retry with en-GB before giving up.
        return new Intl.NumberFormat(DEFAULT_LOCALE, {
            style: 'currency',
            currency: DEFAULT_CURRENCY,
            currencyDisplay: 'narrowSymbol',
            ...extra,
        });
    }
};

export const formatCurrency = (
    amount: number,
    currency: string,
    locale: string = DEFAULT_LOCALE,
): string =>
    buildCurrencyFormat(currency, locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);

export const formatNumber = (
    value: number,
    locale: string = DEFAULT_LOCALE,
): string => {
    try {
        return new Intl.NumberFormat(locale).format(value);
    } catch {
        return new Intl.NumberFormat(DEFAULT_LOCALE).format(value);
    }
};

export const getCurrencySymbol = (
    currency: string,
    locale: string = DEFAULT_LOCALE,
): string => {
    const parts = buildCurrencyFormat(currency, locale).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? safeCurrency(currency);
};

export const formatDate = (
    date: Date,
    timezone: string,
    format: string = 'DD/MM/YYYY',
): string => {
    return moment.utc(date).tz(timezone).format(format);
};

export const formatTime = (
    date: Date,
    timezone: string,
    format: string = 'HH:mm',
): string => {
    return moment.utc(date).tz(timezone).format(format);
};

export const formatDateTime = (
    date: Date,
    timezone: string,
    format: string = 'DD/MM/YYYY HH:mm',
): string => {
    return moment.utc(date).tz(timezone).format(format);
};
